import { Directionality } from '@angular/cdk/bidi';
import { CdkMenu, CdkMenuTrigger, MENU_TRIGGER, PARENT_OR_NEW_MENU_STACK_PROVIDER } from '@angular/cdk/menu';
import { ConnectedPosition, FlexibleConnectedPositionStrategy } from '@angular/cdk/overlay';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DestroyRef, Directive, ElementRef, EventEmitter, forwardRef, inject, Input, Output } from '@angular/core';
import { merge, takeUntil } from 'rxjs';

/** CDK caches its overlay. Refresh its public strategy when inputs change. */
@Directive({
  standalone: true,
  providers: [
    { provide: CdkMenuTrigger, useExisting: forwardRef(() => BrMenuTriggerBehavior) },
    { provide: MENU_TRIGGER, useExisting: forwardRef(() => BrMenuTriggerBehavior) },
    PARENT_OR_NEW_MENU_STACK_PROVIDER,
  ],
})
class BrMenuTriggerBehavior extends CdkMenuTrigger {
  private readonly element = inject<ElementRef<HTMLButtonElement>>(ElementRef);
  private readonly direction = inject(Directionality, { optional: true });

  override open(): void {
    if (this.element.nativeElement.disabled || this.isOpen()) return;
    const strategy = this.overlayRef?.getConfig().positionStrategy;
    if (strategy instanceof FlexibleConnectedPositionStrategy && this.menuPosition) {
      strategy.withPositions(this.menuPosition);
    }
    this.overlayRef?.setDirection(this.direction?.value || 'ltr');
    // CDK caches the portal even when a reused row supplies a new context.
    if (this.menuTemplateRef) this.getMenuContentPortal().context = this.menuData;
    super.open();
    const menu = this.getMenu();
    if (menu instanceof CdkMenu) {
      menu.items.changes.pipe(takeUntil(merge(this.closed, this.destroyed))).subscribe(() => {
        const position = this.overlayRef?.getConfig().positionStrategy;
        if (position instanceof FlexibleConnectedPositionStrategy) {
          // Re-evaluate the viewport after permission/data changes add menu items.
          position.withLockedPosition(false).reapplyLastPosition();
          position.withLockedPosition(true);
        }
      });
    }
  }
}

export interface BrMenuPosition { x?: 'before' | 'after'; y?: 'above' | 'below' }

@Directive({
  selector: 'button[brMenuTriggerFor]',
  standalone: true,
  exportAs: 'brMenuTrigger',
  host: { 'type': 'button' },
  hostDirectives: [{
    directive: BrMenuTriggerBehavior,
    inputs: ['cdkMenuTriggerFor: brMenuTriggerFor', 'cdkMenuTriggerData: brMenuTriggerData'],
    outputs: ['cdkMenuClosed: menuClosed'],
  }],
})
export class BrMenuTriggerDirective {
  private readonly trigger = inject(BrMenuTriggerBehavior);
  private readonly element = inject<ElementRef<HTMLButtonElement>>(ElementRef);

  private readonly destroyRef = inject(DestroyRef);
  @Output() readonly menuOpened = new EventEmitter<void>();

  constructor() {
    // CDK emits before attaching the portal. Notify BR callers after content and
    // initial focus are ready, so a callback can deliberately choose another item.
    let opening = 0;
    this.destroyRef.onDestroy(() => { opening++; this.menuOpened.complete(); });
    this.trigger.closed.pipe(takeUntilDestroyed()).subscribe(() => opening++);
    this.trigger.opened.pipe(takeUntilDestroyed()).subscribe(() => {
      const current = ++opening;
      queueMicrotask(() => {
        if (current === opening && this.isOpen()) {
          this.menuOpened.emit();
        }
      });
    });
  }

  @Input() set brMenuPosition(value: BrMenuPosition) {
    const x = value.x === 'before' ? 'end' : 'start';
    const alternateX = x === 'start' ? 'end' : 'start';
    const y = value.y === 'above' ? 'top' : 'bottom';
    const alternateY = y === 'top' ? 'bottom' : 'top';
    const position = (originX: 'start' | 'end', originY: 'top' | 'bottom'): ConnectedPosition => ({
      originX, overlayX: originX, originY, overlayY: originY === 'top' ? 'bottom' : 'top',
    });
    this.trigger.menuPosition = [position(x, y), position(alternateX, y),
      position(x, alternateY), position(alternateX, alternateY)];
  }

  isOpen(): boolean { return this.trigger.isOpen(); }
  open(): void {
    if (this.element.nativeElement.disabled || this.isOpen()) return;
    this.trigger.open();
    this.trigger.getMenu()?.focusFirstItem('program');
  }

  close(): void {
    if (!this.isOpen()) return;
    this.element.nativeElement.focus();
    this.trigger.close();
  }

  toggle(): void {
    if (this.element.nativeElement.disabled) return;
    if (this.isOpen()) this.close();
    else this.open();
  }
}
